import * as api from '../api';
import { ActionContext } from 'vuex';
import { defineModule, localActionContext } from 'direct-vuex';
import { BaseUser } from '@/models';
import * as Utils from '@/utils';

export interface UserInfo extends BaseUser {
  isSuperAdmin: boolean;
}

export interface AuthState {
  isSignUp: false;
  isAuthenticated: boolean;
  authStatus: AuthStatus;
  user: UserInfo | null;
  failedEmail: string;
  accessToken: string;
}

export enum AuthStatus {
  READY,
  SUCCESS,
  IN_PROGRESS,
  COOKIES_DISABLED,
  FAILURE
}

const getInitialState = (): AuthState => {
  return {
    isSignUp: false,
    isAuthenticated: false,
    user: null,
    authStatus: AuthStatus.READY,
    failedEmail: '',
    accessToken: ''
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-use-before-define
const authActionContext = (context: any) => localActionContext(context, auth);

const login = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context: ActionContext<any, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loginFn: () => Promise<any>
) => {
  const { commit, state } = authActionContext(context);

  if (state.authStatus === AuthStatus.IN_PROGRESS) {
    console.warn(
      'Attempting login while authentication is already in progress'
    );
  }

  commit.updateStatus(AuthStatus.IN_PROGRESS);

  try {
    // There might be a user here from the getStarted workflow
    await api.auth.signOut();

    // The store update is handled by `signInFirebaseUser` which is
    // triggered when the firebaseUser changes (stored by the firebase
    // auth library).
    //
    // `signInFireBaseUser` sets status = SUCCESS;
    await loginFn();
  } catch (error) {
    console.error(error);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((error as any).error === 'idpiframe_initialization_failed') {
      commit.updateStatus(AuthStatus.COOKIES_DISABLED);
    } else {
      commit.updateStatus(AuthStatus.FAILURE);
    }
  }
};

const auth = defineModule({
  namespaced: true as true,
  state: getInitialState() as AuthState,
  getters: {},
  mutations: {
    setAccessToken: (state, token: string) => {
      state.accessToken = token;
    },

    setAuthenticated: (state, userInfo: UserInfo) => {
      state.isAuthenticated = true;
      state.user = userInfo;
    },

    setFailedEmail: (state, email: string) => {
      state.failedEmail = email;
    },

    setUnauthenticated: state => {
      state.isAuthenticated = false;
      state.user = null;
    },

    updateStatus: (state, status: AuthStatus) => {
      state.authStatus = status;
    }
  },
  actions: {
    // Auth happens in two parts:
    // 1. We need to ask google/firebase to authenticate the user and give us back an
    //    id token
    // 2. We need to send that id token to our server to determine which user it belongs
    //    to. Currently we do that by matching on email.
    //
    // However, we always first check if firebase has stored an idToken from
    // the last session so sometimes we only need to do step #2 (the existing token check
    // happens in main.ts)
    async loginWithGoogle(context, idToken) {
      login(context, () => api.auth.signInWithGoogle(idToken));
    },

    async loginWithMicrosoft(context) {
      login(context, () => api.auth.signInWithMicrosoft());
    },

    async signInWithAccessToken(context, accessToken) {
      const { commit } = authActionContext(context);

      try {
        api.auth.setAccessToken(accessToken);
        commit.setAccessToken(accessToken);

        const userInfo = await api.auth.userInfo();

        commit.setAuthenticated(userInfo);
        commit.updateStatus(AuthStatus.SUCCESS);
      } catch (error) {
        console.error(error);

        commit.updateStatus(AuthStatus.FAILURE);

        api.auth.signOut();
      }
    },

    // Listens to the firebase user state.  Connected in main.ts
    async signInFirebaseUser(context, user: firebase.User) {
      const { commit, dispatch } = authActionContext(context);

      // We set IN_PROGRESS here because sometimes we skip the login() call
      // (see login() notes).
      commit.updateStatus(AuthStatus.IN_PROGRESS);

      try {
        const idToken = await user.getIdToken();

        Utils.log({ idToken });

        const accessToken = await api.auth.requestAccessToken(
          idToken,
          'dashboard'
        );

        dispatch.signInWithAccessToken(accessToken);
      } catch (error) {
        console.error(error);

        commit.setFailedEmail(user.email || 'Unknown email');
        commit.updateStatus(AuthStatus.FAILURE);

        api.auth.signOut();
      }
    },

    // Only used to sign in _after_ the get started workflow.
    // Auto sign-ins when directly accessing the dashboard are handled in main.ts
    async signInExistingFirebaseUser(context) {
      const existingUser = api.auth.currentFirebaseUser();

      if (existingUser !== null) {
        authActionContext(context).dispatch.signInFirebaseUser(existingUser);
      }
    },

    async logout(context) {
      const { commit } = authActionContext(context);

      await api.auth.signOut();

      commit.setUnauthenticated();
      context.dispatch('org/clear', null, { root: true });
    }
  }
});

export default auth;
